import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useAuthStore } from "./auth";
import { sendPresence, useWsStore } from "./ws";
import { useWorkspaceStore } from "./workspace";

/**
 * Maps entity_type (from omnios.entity.{kind}.{event_type} subjects)
 * to React Query keys that should be invalidated.
 */
const ENTITY_QUERY_MAP: Record<string, (id: string) => string[][]> = {
	// ── Catalog ───────────────────────────────────
	product: (id) => [
		["product", id],
		["products"],
		["media-urls"],
		["media-urls-list"],
	],
	category: () => [
		["categories"],
		["categories", "roots"],
		["category-properties"],
		["property-definitions"],
	],
	variant: (id) => [["product", id], ["products"]],
	tag: () => [["tags"]],

	// ── Commerce ──────────────────────────────────
	sales_order: (id) => [
		["order", id],
		["orders"],
		["order-analytics"],
	],
	quote: () => [["quotes"], ["quote-reviews"]],
	cart: (id) => [["cart", id]],
	checkout_session: (id) => [["checkout", id]],

	// ── Payments ──────────────────────────────────
	payment_method: () => [["payment-methods"]],
	payment_transaction: (id) => [["transactions"], ["transaction", id]],

	// ── Delivery & Shipping ──────────────────────
	delivery_method: () => [["delivery-methods"]],
	delivery_zone: () => [["delivery-zones"]],
	delivery_rate: () => [["delivery-rates"]],
	shipment: (id) => [["shipments"], ["shipment", id]],

	// ── Inventory ─────────────────────────────────
	warehouse: () => [["warehouses"]],
	inventory_item: (id) => [["inventory"], ["inventory-item", id]],
	inventory_transfer: () => [["inventory-transfers"]],

	// ── Pricing & Promos ─────────────────────────
	discount: () => [["discounts"]],
	promocode: () => [["promocodes"]],
	price_list: () => [["price-lists"]],
	promotion: () => [["promotions"]],
	subscription: (id) => [["subscriptions"], ["subscription", id]],

	// ── Accounting ────────────────────────────────
	journal_entry: (id) => [
		["journal-entries"],
		["journal-entry", id],
		["account-balances"],
	],
	fiscal_period: () => [["fiscal-periods"]],
	account_balance: () => [["account-balances"]],
	recon_session: () => [["recon-sessions"]],

	// ── Manufacturing ─────────────────────────────
	demand_signal: (id) => [
		["manufacturing", "demand-signals"],
		["manufacturing", "demand-signal", id],
	],
	planned_action: (id) => [
		["manufacturing", "actions"],
		["manufacturing", "planned-action", id],
	],
	planning_exception: () => [["manufacturing", "exceptions"]],
	production_order: (_id) => [
		["manufacturing", "production"],
	],
	bom: (id) => [["manufacturing", "boms"], ["manufacturing", "bom", id]],

	// ── Workflow ───────────────────────────────────
	work_item: (id) => [
		["work-item", id],
		["work-items"],
		["work-items", "subtasks"],
	],
	pipeline_run: (id) => [
		["pipeline-runs"],
		["pipeline-runs", id],
		["pipeline-run-logs", id],
		["pipeline", id],
		["products"],
		["work-items", "subtasks"],
		["enrichment-history"],
		["enrichment-stats"],
	],

	// ── Agent ─────────────────────────────────────
	agent_run: (id) => [["agent-run", id], ["agent-runs"]],
	agent_session: (id) => [
		["agent-sessions"],
		["agent-session", id],
		["agent-session-files", id],
	],
	agent_file: (id) => [["agent-session-files", id], ["agent-memory-files"]],

	// ── Communication ─────────────────────────────
	message: () => [["messages"]],
	notification: () => [["notification-preferences"], ["notification-unread-count"]],

	// ── Knowledge ─────────────────────────────────
	knowledge_document: () => [["knowledge-documents"], ["knowledge-chunks"]],

	// ── IAM ───────────────────────────────────────
	member: () => [["members"]],
	role: () => [["roles"]],

	// ── Portal ────────────────────────────────────
	portal_instance: (id) => [["portal-instances"], ["portal-instance", id]],
};

/**
 * Maps NATS KV bucket names → query key prefixes to invalidate.
 * When a KV bucket changes, all matching queries auto-refetch.
 */
const KV_BUCKET_QUERY_MAP: Record<string, string[][]> = {
	// Accounting
	BALANCE: [["accounting", "balance"], ["accounting", "entries"]],
	CASH_POSITION: [["accounting", "cash-position"]],
	TRIAL_BALANCE: [["accounting", "trial-balance"]],
	BUDGET_VARIANCE: [["accounting", "budget"]],
	// Inventory
	INVENTORY_ATP: [["inventory"]],
	// Manufacturing
	PLANNING_STATUS: [["manufacturing", "actions"]],
	EXCEPTION_SUMMARY: [["manufacturing", "exceptions"]],
};

const KV_DEBOUNCE_MS: Record<string, number> = {
	BALANCE: 500,
	CASH_POSITION: 500,
	INVENTORY_ATP: 1000,
	PLANNING_STATUS: 300,
	EXCEPTION_SUMMARY: 300,
};

export function useRealtime() {
	const qc = useQueryClient();
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
	const wsId = useWorkspaceStore((s) => s.selectedId);
	const currentPage = useRef("");
	const lastSentPage = useRef("");
	const kvTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

	useEffect(() => {
		if (!isAuthenticated) return;

		const store = useWsStore.getState();
		store.connect();

		// Entity event → query invalidation
		const unsubEntity = store.subscribe((evt) => {
			const mapper = ENTITY_QUERY_MAP[evt.entity_type];
			if (mapper) {
				const keys = mapper(evt.entity_id);
				for (const key of keys) {
					qc.invalidateQueries({ queryKey: key });
				}
			}

			const action = evt.action ?? "";
			if (
				action.includes("Created") ||
				action.includes("Updated") ||
				action.includes("Deleted")
			) {
				qc.invalidateQueries({
					queryKey: ["entity-activity", evt.entity_type, evt.entity_id],
				});
				qc.invalidateQueries({ queryKey: ["global-activity"] });
			}
		});

		// KV bucket change → debounced query invalidation
		const unsubKv = store.subscribeKv((update) => {
			// Only process updates for the active workspace
			if (wsId && update.workspace_id && update.workspace_id !== wsId) {
				return;
			}

			const queryKeys = KV_BUCKET_QUERY_MAP[update.bucket];
			if (!queryKeys) return;

			const debounce = KV_DEBOUNCE_MS[update.bucket] ?? 500;

			// Debounce per bucket
			const existing = kvTimers.current.get(update.bucket);
			if (existing) clearTimeout(existing);

			kvTimers.current.set(
				update.bucket,
				setTimeout(() => {
					for (const key of queryKeys) {
						qc.invalidateQueries({ queryKey: key });
					}
					kvTimers.current.delete(update.bucket);
				}, debounce),
			);
		});

		return () => {
			unsubEntity();
			unsubKv();
			for (const timer of kvTimers.current.values()) {
				clearTimeout(timer);
			}
			kvTimers.current.clear();
		};
	}, [isAuthenticated, qc, wsId]);

	const setPage = useCallback((page: string) => {
		currentPage.current = page;
		// Only send presence when page actually changes
		if (page !== lastSentPage.current) {
			lastSentPage.current = page;
			sendPresence(page);
		}
	}, []);

	return { setPage };
}
